<template>
    <aside class="patient-sidebar">
        <div class="sidebar-section">
            <p class="sidebar-heading">PATIENT RECORD</p>

            <div v-for="item in patientItems" :key="item.key" class="sidebar-item-wrapper">
                <div
                    :class="[
                        'sidebar-item',
                        {
                            'sidebar-item--active': activeTab === item.key,
                            'sidebar-item--disabled': isItemDisabled(item),
                        },
                    ]"
                    @click="openTab(item)"
                >
                    <div class="sidebar-item__left">
                        <common-icon :name="item.icon" size="small"></common-icon>
                        <span>{{ item.label }}</span>
                    </div>

                    <common-icon
                        name="leftarrow"
                        size="tiny"
                        :class="['sidebar-item__arrow', { 'sidebar-item__arrow--active': activeTab === item.key }]"
                    ></common-icon>
                </div>
                <div v-if="isItemDisabled(item)" class="sidebar-tooltip">
                    {{ getItemTooltip(item) }}
                </div>
            </div>
        </div>

        <div class="sidebar-section">
            <p class="sidebar-heading">ACTIONS</p>

            <div v-for="item in actionItems" :key="item.key" class="sidebar-item-wrapper">
                <div
                    :class="[
                        'sidebar-item',
                        'sidebar-item--action',
                        {
                            'sidebar-item--active': activeTab === item.key,
                            'sidebar-item--delete': item.key === 'delete-patient',
                            'sidebar-item--disabled': isItemDisabled(item),
                        },
                    ]"
                    @click="openTab(item)"
                >
                    <div class="sidebar-item__left">
                        <common-icon :name="item.icon" size="small"></common-icon>
                        <span>{{ item.label }}</span>
                    </div>

                    <common-icon
                        name="leftarrow"
                        size="tiny"
                        :class="[
                            'sidebar-item__arrow',
                            {
                                'sidebar-item__arrow--active': activeTab === item.key,
                            },
                        ]"
                    ></common-icon>
                </div>
                <div v-if="isItemDisabled(item)" class="sidebar-tooltip">
                    {{ getItemTooltip(item) }}
                </div>
            </div>
        </div>
    </aside>
</template>

<script>
    export default {
        props: {
            activeTab: {
                type: String,
                default: 'profile',
            },
            patientId: {
                type: [String, Number],
                default: null,
            },
        },
        data() {
            return {
                patientItems: [
                    {
                        key: 'profile',
                        label: this.$t('modals.patient.tab.label_three'),
                        icon: 'user',
                    },
                    {
                        key: 'appointments',
                        label: this.$t('modals.patient.tab.label_one'),
                        icon: 'timeline',
                    },
                    {
                        key: 'documents',
                        label: this.$t('modals.patient.tab.label_two'),
                        icon: 'file',
                    },
                    {
                        key: 'summary',
                        label: this.$t('modals.patient.tab.label_four'),
                        icon: 'summary',
                        isDisabled: true,
                        tooltip: this.$t('modals.patient.feature_coming_soon'),
                    },
                ],

                actionItems: [
                    {
                        key: 'request-records',
                        label: this.$t('modals.patient_profile.profile.detail_form.tabs.request'),
                        icon: 'record',
                    },
                    {
                        key: 'record-requests-inbox',
                        label: this.$t('modals.patient_profile.profile.detail_form.tabs.request_inbox'),
                        icon: 'sms',
                        isDisabled: true,
                        tooltip: this.$t('modals.patient.feature_coming_soon'),
                    },
                    {
                        key: 'general-intake',
                        label: this.$t('modals.sidebarList.content.general_intake'),
                        icon: 'document',
                    },
                    {
                        key: 'accident-intake',
                        label: this.$t('modals.sidebarList.content.accident_intake'),
                        icon: 'file',
                    },
                    {
                        key: 'hipaa-forms',
                        label: this.$t('modals.patient_profile.profile.detail_form.tabs.hipaa'),
                        icon: 'insure',
                    },
                    {
                        key: 'refer-patient',
                        label: this.$t('modals.refer_patient.buttons.refer'),
                        icon: 'add-user',
                    },
                    {
                        key: 'message-patient',
                        label: this.$t('modals.sidebarList.content.message'),
                        icon: 'chat',
                    },
                    {
                        key: 'delete-patient',
                        label: this.$t('modals.sidebarList.content.remove'),
                        icon: 'trash-bin',
                    },
                ],
            }
        },
        computed: {
            hasPatient() {
                return !!(this.patientId || this.$route.params.id)
            },
        },
        methods: {
            isItemDisabled(item) {
                if (!this.hasPatient && item.key !== 'profile') {
                    return true
                }
                return !!item.isDisabled
            },
            getItemTooltip(item) {
                if (!this.hasPatient && item.key !== 'profile') {
                    return 'Please save patient profile first'
                }
                return item.tooltip || this.$t('modals.patient.feature_coming_soon')
            },
            openTab(item) {
                const key = typeof item === 'object' ? item.key : item
                if (this.isItemDisabled(item)) {
                    return
                }
                this.$emit('toggle', key)
            },
        },
    }
</script>
<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .patient-sidebar {
        width: 260px;
        background: #fff;
        border: 1px solid #eceff3;
        border-radius: 12px;
        padding: 20px 0;

        display: flex;
        flex-direction: column;

        height: auto;
        align-self: stretch;
    }

    .sidebar-section {
        display: flex;
        flex-direction: column;
    }
    .sidebar-section + .sidebar-section {
        padding-top: 12px;
    }

    .sidebar-heading {
        padding: 0 30px;
        color: #99a1af;
        font-size: 12px;
        font-weight: 500;
        letter-spacing: 0.08em;
        text-transform: uppercase;
    }

    .sidebar-item-wrapper {
        position: relative;

        &:hover .sidebar-tooltip {
            visibility: visible;
            opacity: 1;
            transform: translateY(-50%) scale(1);
        }
    }

    .sidebar-item {
        display: flex;
        align-items: center;
        justify-content: space-between;

        margin: 2px 8px;
        padding: 12px 20px;

        border-radius: 10px;
        cursor: pointer;
        transition: background-color 0.2s ease, color 0.2s ease;

        color: #4a5565;
        font-size: 14px;
        font-weight: 500;

        &:hover {
            background: #f9fafb;
            color: #111827;

            i,
            .sidebar-item__arrow {
                color: #111827;
            }
        }
    }

    .sidebar-item--disabled {
        cursor: not-allowed !important;
        opacity: 0.55;
        color: #94a3b8 !important;

        i {
            color: #94a3b8 !important;
        }

        &:hover {
            background: transparent !important;
            color: #94a3b8 !important;

            i,
            .sidebar-item__arrow {
                color: #94a3b8 !important;
            }
        }
    }

    .sidebar-item__left {
        display: flex;
        align-items: center;
        gap: 12px;

        i {
            color: #99a1af;
            transition: color 0.2s ease;
        }
    }

    .sidebar-item__arrow {
        opacity: 0;
        color: inherit;
        transition: opacity 0.2s ease;
    }

    .sidebar-item__arrow--active {
        opacity: 1;
    }

    .sidebar-item--active:not(.sidebar-item--delete) {
        background: #eff6ff;
        color: #1447e6;
        font-weight: 500;

        i {
            color: #1447e6;
        }
    }
    .sidebar-item--delete {
        color: #fb2c36;

        i {
            color: #fb2c36;
        }

        &:hover {
            background: #fef2f2;
            color: #b91c1c;

            i,
            .sidebar-item__arrow {
                color: #b91c1c;
            }
        }
    }
    .sidebar-item--delete.sidebar-item--active {
        background: #fef2f2;
        color: #e7000b;

        i,
        .sidebar-item__arrow {
            color: #e7000b;
        }
    }

    .sidebar-tooltip {
        visibility: hidden;
        opacity: 0;
        position: absolute;
        left: calc(100% + 10px);
        top: 50%;
        transform: translateY(-50%) scale(0.95);
        background-color: #1e293b;
        color: #f8fafc;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 400;
        white-space: nowrap;
        pointer-events: none;
        transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
        z-index: 1000;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);

        &::after {
            content: '';
            position: absolute;
            top: 50%;
            right: 100%;
            margin-top: -5px;
            border-width: 5px;
            border-style: solid;
            border-color: transparent #1e293b transparent transparent;
        }
    }

    @media (max-width: 992px) {
        .patient-sidebar {
            width: 100%;
        }

        .sidebar-tooltip {
            left: 50%;
            top: auto;
            bottom: calc(100% + 6px);
            transform: translateX(-50%) scale(0.95);

            &::after {
                top: 100%;
                left: 50%;
                right: auto;
                margin-top: 0;
                margin-left: -5px;
                border-color: #1e293b transparent transparent transparent;
            }
        }

        .sidebar-item-wrapper:hover .sidebar-tooltip {
            transform: translateX(-50%) scale(1);
        }
    }
</style>
